import { useQuery } from 'react-query';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CircularProgress } from '@mui/material';

import { AdminDashboardApi } from '@app/domain/dashboard/api/AdminDashboardApi';
import { AdminDashboardCohortRowOutDto } from '@app/swagger-types';
import { clsxm } from '@app/styles/clsxm';

import { DashboardCard } from '../shared/DashboardCard';
import { useChartTable } from '../shared/useChartTable';
import { DASHBOARD_COLOR } from '../shared/dashboardColors';

const fmt = (n: number) => n.toLocaleString('nl-NL');
const weekLabel = (iso: string) => format(parseISO(iso), 'd MMM', { locale: nl });

const COHORT_SERIES = [
  { key: 'firstCodePercent' as const, color: DASHBOARD_COLOR.cat1, label: 'Eerste code' },
  { key: 'firstScanPercent' as const, color: DASHBOARD_COLOR.cat2, label: 'Eerste scan' },
  { key: 'paidPercent' as const, color: DASHBOARD_COLOR.cat3, label: 'Betaald' },
];

// admin-dashboard-opdracht.md §4.4 - "het belangrijkste blok van het scherm", en het meeste
// rekenwerk aan de backend-kant (zie CohortStatsWeeklyRepository/CohortStatsBuildService). Every
// chart here respects the top-level period selector except sourceConversion90d, which the
// opdracht itself fixes at 90 days regardless of the selector.
export const DashboardFunnelBand: React.FC<{ from: string; to: string }> = ({ from, to }) => {
  const query = useQuery(['admin-dashboard-funnel', from, to], () => AdminDashboardApi.getFunnel(from, to));
  const cohortToggle = useChartTable();
  const sourceToggle = useChartTable();

  if (query.isLoading) {
    return (
      <div className="flex h-[280px] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  if (!query.data) {
    return null;
  }

  const { cohortTrend, lastFullCohortWeekStart, lastFullCohortFunnel, sourcesPerWeek, sourceConversion90d } =
    query.data;
  const firstImmatureIdx = cohortTrend.findIndex((r) => !r.mature);
  const funnelMax = lastFullCohortFunnel[0]?.count ?? 1;

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[1.35fr_1fr]">
        <DashboardCard
          title="Bereikte stap per cohortweek"
          subtitle="Percentage van iedereen die zich in die week aanmeldde"
          headerRight={cohortToggle.toggleButton}
        >
          <div className="mb-2 flex flex-wrap gap-4 text-[12.5px] text-gray-600">
            {COHORT_SERIES.map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1.5">
                <i className="inline-block h-[9px] w-[9px] rounded-sm" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
          {cohortToggle.showTable ? (
            <CohortTable rows={cohortTrend} />
          ) : (
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cohortTrend} margin={{ top: 8, right: 30, left: 0 }}>
                  <CartesianGrid stroke={DASHBOARD_COLOR.grid} vertical={false} />
                  {firstImmatureIdx > 0 && cohortTrend.length > 0 && (
                    <ReferenceArea
                      x1={cohortTrend[firstImmatureIdx].cohortWeekStart}
                      x2={cohortTrend[cohortTrend.length - 1].cohortWeekStart}
                      fill="#f3f4f6"
                      fillOpacity={0.75}
                      ifOverflow="extendDomain"
                    />
                  )}
                  <XAxis
                    dataKey="cohortWeekStart"
                    tickFormatter={weekLabel}
                    tickLine={false}
                    axisLine={false}
                    fontSize={11}
                    stroke={DASHBOARD_COLOR.muted}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    fontSize={11}
                    width={34}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <RTooltip
                    cursor={{ stroke: DASHBOARD_COLOR.grid }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) {
                        return null;
                      }
                      const row = payload[0].payload as AdminDashboardCohortRowOutDto;
                      return (
                        <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] leading-relaxed text-white">
                          <b>Week van {weekLabel(label as string)}</b>
                          {!row.mature && <span className="opacity-70"> (nog niet uitgerijpt)</span>}
                          <br />
                          {COHORT_SERIES.map((s) => (
                            <span key={s.key} className="mr-2 inline-flex items-center gap-1">
                              <i className="inline-block h-2 w-2 rounded-sm" style={{ background: s.color }} />
                              {s.label}: <b>{row[s.key].toFixed(1)}%</b>
                            </span>
                          ))}
                        </div>
                      );
                    }}
                  />
                  {COHORT_SERIES.map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 3.5, fill: s.color, stroke: '#fff', strokeWidth: 2 }}
                      isAnimationActive={false}
                      label={(props: { x: number; y: number; index: number }) =>
                        props.index === cohortTrend.length - 1 ? (
                          <text
                            x={props.x - 4}
                            y={props.y - 8}
                            textAnchor="end"
                            fontSize={11}
                            fontWeight={600}
                            fill={s.color}
                          >
                            {s.label}
                          </text>
                        ) : (
                          <g key={`empty-${props.index}`} />
                        )
                      }
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="mt-2 text-[11.5px] text-gray-400">
            Uitgegrijsde cohorten zijn nog niet uitgerijpt - wie zich recent aanmeldde, kán nog niet betaald hebben.
          </p>
        </DashboardCard>

        <DashboardCard
          title="Laatste volledige cohort"
          subtitle={
            lastFullCohortWeekStart
              ? `Week van ${weekLabel(lastFullCohortWeekStart)} — ${fmt(
                  lastFullCohortFunnel[0]?.count ?? 0
                )} aanmeldingen`
              : 'Nog geen uitgerijpt cohort in deze periode'
          }
        >
          {lastFullCohortFunnel.length ? (
            <div className="mt-1">
              {lastFullCohortFunnel.map((step, i) => (
                <div key={step.label} className="mb-3.5">
                  <div className="mb-1 flex items-center justify-between text-[13px]">
                    <b className="text-gray-800">{step.label}</b>
                    <span className="text-[12.5px] text-gray-400">
                      {fmt(step.count)}
                      {step.percentOfPrevious !== undefined &&
                        ` · ${step.percentOfPrevious.toFixed(0)}% van vorige stap`}
                    </span>
                  </div>
                  <div className="h-[15px] overflow-hidden rounded bg-[#eef1f5]">
                    <div
                      className="h-full rounded"
                      style={{
                        width: `${(step.count / funnelMax) * 100}%`,
                        background:
                          i === 0
                            ? '#9aa3af'
                            : [DASHBOARD_COLOR.cat1, DASHBOARD_COLOR.cat2, DASHBOARD_COLOR.cat3][i - 1],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-6 text-center text-gray-400">Nog geen volledig uitgerijpt cohort beschikbaar.</p>
          )}
          <p className="mt-1 text-[11.5px] text-gray-400">
            Het percentage staat telkens ten opzichte van de vorige stap - daar zie je wáár ze afhaken.
          </p>
        </DashboardCard>
      </div>

      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
        <DashboardCard
          title="Aanmeldingen per bron"
          subtitle="Per week, gestapeld"
          headerRight={sourceToggle.toggleButton}
        >
          <div className="mb-2 flex flex-wrap gap-4 text-[12.5px] text-gray-600">
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-[9px] w-[9px] rounded-sm" style={{ background: DASHBOARD_COLOR.cat1 }} />
              Google Ads
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-[9px] w-[9px] rounded-sm" style={{ background: DASHBOARD_COLOR.cat2 }} />
              Google-login
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-[9px] w-[9px] rounded-sm" style={{ background: DASHBOARD_COLOR.cat3 }} />
              E-mail
            </span>
          </div>
          {sourceToggle.showTable ? (
            <SourceTable rows={sourcesPerWeek} />
          ) : (
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sourcesPerWeek} margin={{ top: 8, right: 8 }}>
                  <CartesianGrid stroke={DASHBOARD_COLOR.grid} vertical={false} />
                  <XAxis
                    dataKey="weekStart"
                    tickFormatter={weekLabel}
                    tickLine={false}
                    axisLine={false}
                    fontSize={11}
                    stroke={DASHBOARD_COLOR.muted}
                  />
                  <YAxis tickLine={false} axisLine={false} allowDecimals={false} fontSize={11} width={32} />
                  <RTooltip
                    cursor={false}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) {
                        return null;
                      }
                      const row = payload[0].payload as typeof sourcesPerWeek[number];
                      const total = row.googleAds + row.googleLogin + row.email;
                      return (
                        <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] leading-relaxed text-white">
                          <b>{weekLabel(label as string)}</b>
                          <br />
                          Google Ads: <b>{fmt(row.googleAds)}</b>
                          <br />
                          Google-login: <b>{fmt(row.googleLogin)}</b>
                          <br />
                          E-mail: <b>{fmt(row.email)}</b>
                          <br />
                          <span className="opacity-65">Totaal: {fmt(total)}</span>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="googleAds" stackId="s" fill={DASHBOARD_COLOR.cat1} />
                  <Bar dataKey="googleLogin" stackId="s" fill={DASHBOARD_COLOR.cat2} />
                  <Bar dataKey="email" stackId="s" fill={DASHBOARD_COLOR.cat3} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </DashboardCard>

        <DashboardCard title="Conversie per bron" subtitle="Aanmeldingen die uiteindelijk betaalden - laatste 90 dagen">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-gray-400">
                <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Bron</th>
                <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Aanmeldingen</th>
                <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Betaald</th>
                <th className="py-2 text-right font-semibold uppercase tracking-wide">Conversie</th>
              </tr>
            </thead>
            <tbody>
              {sourceConversion90d.map((row, i) => (
                <tr key={row.source} className={clsxm(i % 2 === 1 && 'bg-gray-50')}>
                  <td className="py-1.5 pr-3">{row.source}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{fmt(row.signups)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{fmt(row.paid)}</td>
                  <td className="py-1.5 text-right tabular-nums">{row.conversionPercent.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11.5px] text-gray-400">
            Een bron die veel aanmeldingen levert en weinig converteert, kost geld in plaats van dat hij het oplevert.
          </p>
        </DashboardCard>
      </div>
    </div>
  );
};

const CohortTable: React.FC<{ rows: AdminDashboardCohortRowOutDto[] }> = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-gray-200 text-gray-400">
          <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Cohortweek</th>
          <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Eerste code</th>
          <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Eerste scan</th>
          <th className="py-2 text-right font-semibold uppercase tracking-wide">Betaald</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.cohortWeekStart} className={clsxm(i % 2 === 1 && 'bg-gray-50')}>
            <td className="py-1.5 pr-3">
              {weekLabel(r.cohortWeekStart)}
              {!r.mature && (
                <span className="ml-1.5 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">
                  niet uitgerijpt
                </span>
              )}
            </td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{r.firstCodePercent.toFixed(1)}%</td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{r.firstScanPercent.toFixed(1)}%</td>
            <td className="py-1.5 text-right tabular-nums">{r.paidPercent.toFixed(1)}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SourceTable: React.FC<{
  rows: { weekStart: string; googleAds: number; googleLogin: number; email: number }[];
}> = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-gray-200 text-gray-400">
          <th className="py-2 pr-3 font-semibold uppercase tracking-wide">Week</th>
          <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Google Ads</th>
          <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">Google-login</th>
          <th className="py-2 pr-3 text-right font-semibold uppercase tracking-wide">E-mail</th>
          <th className="py-2 text-right font-semibold uppercase tracking-wide">Totaal</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.weekStart} className={clsxm(i % 2 === 1 && 'bg-gray-50')}>
            <td className="py-1.5 pr-3">{weekLabel(r.weekStart)}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{r.googleAds}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{r.googleLogin}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums">{r.email}</td>
            <td className="py-1.5 text-right tabular-nums">{r.googleAds + r.googleLogin + r.email}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
